/**
 * Pure utility functions extracted from Lists.js for testability.
 * These functions have no React dependencies, no hooks, no JSX.
 */
import { toNumber } from '../../core/utilities/Utilities.js';

export const sanitizeId = (id) => encodeURIComponent(String(id ?? ''));

export const getLocale = (application, key) => {
    const globalData = application?.globalData;
    if (!globalData || !('lang' in globalData)) {
        return 'MISSING_LANG_IN_GLOBAL_DATA';
    }
    const { strings } = globalData;
    if (!strings || !(key in strings)) {
        return `MISSING_${key}_IN_STRINGS`;
    }
    return strings[key];
};

export const getSalesOrderStatusColor = (status) => {
    const map = {
        DRAFT: 'darkorange',
        CANCELED: 'darkred',
        INVOICING_INTEGRATION_ERROR: 'darkred',
        LOGISTICS_INTEGRATION_ERROR: 'darkred',
        INVALIDATED: 'darkred',
        INVOICED: 'darkmagenta',
        NO_INVOICING: 'darkgreen',
        ON_DEBT_COLLECTION: 'darkred',
        OVERDUE: 'darkred',
        PAID: 'darkblue',
        SUBMITTED: 'darkgreen',
        PAYMENT_REMINDER_SENT: 'darkred',
    };
    return map[status] ?? 'inherit';
};

export const getSalesOrderDeliveryStatusColor = (deliveryStatus) => {
    const map = {
        NEW: 'inherit',
        CANCELED: 'darkred',
        IN_PROGRESS: 'darkgreen',
        COMPLETED: 'darkblue',
        NOT_FOUND: 'darkred',
        WAITING_FOR_ARRIVAL: 'darkorange',
    };
    return map[deliveryStatus] ?? 'inherit';
};

export const getStorageOrderStatusColor = (status) => {
    const map = {
        DRAFT: 'darkorange',
        CANCELED: 'darkred',
        SUBMITTED: 'darkgreen',
        INVOICING_INTEGRATION_ERROR: 'darkred',
        LOGISTICS_INTEGRATION_ERROR: 'darkred',
    };
    return map[status] ?? 'inherit';
};

export const getManufacturingOrderStatusColor = (status) => {
    const map = {
        DRAFT: 'darkorange',
        IN_PROGRESS: 'darkgreen',
        PRODUCED: 'darkblue',
        CANCELLED: 'darkred',
    };
    return map[status] ?? 'inherit';
};

// Sales order demand (Collico ordered + draft reservations) not covered by warehouse stock.
const warehouseShortfall = (row) => {
    return Math.max(
        0,
        toNumber(row.total_ordered_amount) +
        toNumber(row.reserved_quantity) -
        toNumber(row.stock_balance)
    );
};

// Warehouse (Collico) only. Demand beyond the warehouse stock is taken from pre-sales instead,
// so this never goes below zero.
export const computeStockAvailable = (row) => {
    return Math.max(
        0,
        toNumber(row.stock_balance) -
        toNumber(row.reserved_quantity) -
        toNumber(row.total_ordered_amount)
    );
};

// Quantities are whole units or partial kegs (x.xx) - adding decimals in JS leaves float noise
// (0.1 + 0.2 = 0.30000000000000004), so sums are rounded back to 2 decimals.
export const roundQuantity = (value) => Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;

// Everything not yet in the warehouse: still to be produced on open MOs (pre_sales_quantity) +
// brewery stock + in transit to the warehouse, minus the sales demand the warehouse can't cover.
// All values in this row's own unit (packs for MULTIPACK SKUs). Only for SKUs produced through MOs.
export const computePreSalesAvailable = (row) => {
    if (!row.mo_tracked) {
        return 0;
    }
    return roundQuantity(
        toNumber(row.pre_sales_quantity) +
        toNumber(row.brewery_stock_balance) +
        toNumber(row.in_transit_quantity) -
        warehouseShortfall(row)
    );
};

export const getterAssocField = (value, row, entity) => {
    return row?.[entity] ? row?.[entity].map(obj => obj.name).join(',') : '';
};

// Batch.amount_planned is in hectoliters (its locale label is "Planned (hl)" despite the
// field/code name saying "Liters") - total_planned_liters (sum of each Manufacturing
// Order's planned_quantity * that SKU's pack_amount, both already in liters) is compared
// against amount_planned*100 to flag when Sales has planned more packaging than the tank
// is expected to yield. Informational only - no hard block requested (yet).
export const computeBatchCapacityUsage = (row) => {
    const capacityLiters = toNumber(row.amount_planned) * 100;
    const plannedLiters = toNumber(row.total_planned_liters);
    return {
        capacityLiters,
        plannedLiters,
        exceeds: capacityLiters > 0 && plannedLiters > capacityLiters,
        excessLiters: Math.max(0, plannedLiters - capacityLiters),
    };
};

export const makeGridDateValueGetter = () => (value) => value ? new Date(value) : null;
